import { type RefObject, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Popover } from 'react-aria-components';
import { css } from '@understory-io/leaf/css';
import { Dialog } from '@understory-io/leaf/dialog';
import {
  AvailabilityCalendar,
  type DayAvailability,
} from '~/ui/availability-calendar';
import { token } from '~/styled-system/tokens';
import { ampli } from '~/domain/plugins/analytics';
import { useAvailability } from '~/ui/availability-calendar/use-availability';

type DatePickerProps = {
  experienceId: string;
  selectedDate: string;
  /** Date the calendar should open on — typically the first upcoming event
   *  from the EventList. */
  initialFocusDate?: string | null;
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  onDateSelect: (date: string) => void;
  /** Availability filters (languages, locations, duration, tickets) — make the
   *  calendar dots reflect the current selection. `duration` (seconds) applies to
   *  open-hour bookings only; fixed events ignore it. */
  filters?: {
    languages?: string[];
    locationIds?: string[];
    duration?: number;
    tickets?: { [variantId: string]: number };
  };
};

export type { DatePickerProps };

export function DatePicker({
  experienceId,
  selectedDate,
  initialFocusDate,
  triggerRef,
  onClose,
  onDateSelect,
  filters,
}: DatePickerProps) {
  const { availability, readyMonths, failedMonths, retry, handleMonthChange } =
    useAvailability({
      experienceId,
      initialFocusDate,
      filters,
    });

  const viewSize = window.matchMedia(
    `(min-width: ${token('breakpoints.tablet')})`
  );

  if (viewSize.matches) {
    return (
      <Popover
        triggerRef={triggerRef}
        isOpen
        onOpenChange={(open) => {
          if (!open) {
            ampli.checkoutDateSelectionDialogClosed();
            onClose();
          }
        }}
        placement="bottom right"
        className={popoverStyle}
      >
        <AvailabilityCalendar
          selectedDate={selectedDate}
          availability={availability}
          readyMonths={readyMonths}
          failedMonths={failedMonths}
          onRetry={retry}
          initialFocusDate={initialFocusDate}
          onDateSelect={(date) => {
            onDateSelect(date);
            onClose();
          }}
          onMonthChange={handleMonthChange}
        />
      </Popover>
    );
  }

  return (
    <DatePickerModal
      selectedDate={selectedDate}
      availability={availability}
      readyMonths={readyMonths}
      failedMonths={failedMonths}
      onRetry={retry}
      initialFocusDate={initialFocusDate}
      onClose={onClose}
      onDateSelect={onDateSelect}
      onMonthChange={handleMonthChange}
    />
  );
}

function DatePickerModal({
  selectedDate,
  availability,
  readyMonths,
  failedMonths,
  onRetry,
  initialFocusDate,
  onClose,
  onDateSelect,
  onMonthChange,
}: {
  selectedDate: string;
  availability: Record<string, DayAvailability>;
  readyMonths: ReadonlySet<string>;
  failedMonths: ReadonlySet<string>;
  onRetry: () => void;
  initialFocusDate?: string | null;
  onClose: () => void;
  onDateSelect: (date: string) => void;
  onMonthChange: (startOfMonth: string) => void;
}) {
  const [localDate, setLocalDate] = useState<string | null>(selectedDate);

  function handleConfirm() {
    if (!localDate) return;
    onDateSelect(localDate);
    onClose();
  }

  return (
    <Dialog
      title={
        <FormattedMessage
          defaultMessage="Select date"
          description="Date picker dialog heading"
        />
      }
      onClose={() => {
        ampli.checkoutDateSelectionDialogClosed();
        onClose();
      }}
      primaryAction={{
        appearance: 'primary',
        onClick: handleConfirm,
        disabled: !localDate,
        children: (
          <FormattedMessage
            defaultMessage="Select"
            description="Date picker confirm button"
          />
        ),
      }}
    >
      <AvailabilityCalendar
        selectedDate={localDate}
        availability={availability}
        readyMonths={readyMonths}
        failedMonths={failedMonths}
        onRetry={onRetry}
        initialFocusDate={initialFocusDate}
        onDateSelect={(date) => setLocalDate(date)}
        onMonthChange={onMonthChange}
      />
    </Dialog>
  );
}

const popoverStyle = css({
  background: 'surface.raised',
  borderRadius: 'large',
  boxShadow: '[0 4px 24px rgba(0, 0, 0, 0.12)]',
  padding: 'medium',
  outline: 'none',
});
